/* eslint-disable no-param-reassign, max-params, max-lines */
import { api } from "jiffy-api";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { track } from "common/utils/mixpanel/tracker";
import { setDefaultFallback } from "../utils/products";
import { EVENTS } from "../utils/tracker";

const initialState = {
  initialProduct: null,
  initialSelection: null,
  // fastest T-Shirts atm
  topItems: [],
  // second row of products, fastest sweatshirts
  fastestItems: [],
  // last row of products
  trending: [],
  prices: {},
  selectedProduct: null,
  selectedComposition: null,
  inventory: {},
  productsSelectedComposition: {},
  variantQuantities: {},
  assumptiveQuantity: 10,
  freeShippingThreshold: null,
  bulkDiscountThreshold: null
};

const productModule = {
  namespaced: true,

  state() {
    return { ...initialState };
  },

  getters: {
    topItems: state => state.topItems,
    fastestItems: state => state.fastestItems,
    trending: state => state.trending,
    freeShippingThreshold: state => state.freeShippingThreshold,
    bulkDiscountThreshold: state => state.bulkDiscountThreshold,
    selectedProduct: state => state.selectedProduct,
    selectedComposition: state => {
      if (state.selectedComposition) return state.selectedComposition;
      return state.selectedProduct?.compositions.find(comp => comp.default);
    },
    selectedCompositionId: (_, getters) => getters.selectedComposition?.id || 9286, // default composition id
    selectedCompositionSurfaces: (_, getters) => getters.selectedComposition?.surfaces,
    selectedCompositionVariants: (_, getters) => getters.selectedComposition?.variants,
    prices: state => state.prices,
    selectedColor: state => state.selectedColor,
    selectedCompQuantities: (state, getters) => {
      const { variantQuantities, selectedCompositionId } = getters;

      return variantQuantities[selectedCompositionId] || {};
    },
    variantQuantities: state => state.variantQuantities,
    compositionsWithQty: state => Object.keys(state.variantQuantities),
    flatVariantsQty: state =>
      Object.values(state.variantQuantities).reduce(
        (res, compositionVariants) => ({
          ...res,
          ...compositionVariants
        }),
        {}
      ),
    flatPrices: state =>
      Object.values(state.prices).reduce(
        (res, compositionVariants) => ({
          ...res,
          ...compositionVariants
        }),
        {}
      ),
    totalItems: (state, getters) => {
      const { flatVariantsQty } = getters;
      return Object.values(flatVariantsQty).reduce((res, qty) => res + qty, 0);
    },
    allProducts: (state, getters) => {
      const { topItems, fastestItems, trending } = getters;
      return [...topItems, ...fastestItems, ...trending];
    },
    getProductById: (_, getters) => id => getters.allProducts.find(product => product.id === id),
    totalsNoDiscount: (_, getters) => {
      const { flatPrices, flatVariantsQty } = getters;

      return Object.keys(flatVariantsQty).reduce((res, variantId) => {
        const qty = flatVariantsQty[variantId];
        const tierZeroPrice = flatPrices[variantId]?.[0] || 0;
        if (tierZeroPrice) {
          return res + qty * tierZeroPrice;
        }
        return res;
      }, 0);
    },
    compositionsQtys: (_, getters) => {
      const { variantQuantities } = getters;
      return Object.keys(variantQuantities).reduce(
        (res, compId) => ({
          ...res,
          [compId]: Object.values(variantQuantities[compId]).reduce((sum, qty) => sum + qty, 0)
        }),
        {}
      );
    },
    totalQty: (_, getters) =>
      Object.values(getters.compositionsQtys).reduce((res, qty) => res + Number(qty), 0),
    productsSelectedComposition: state => state.productsSelectedComposition,
    assumptiveQuantity: state => state.assumptiveQuantity,
    assumptiveDefaultVariant: (_, getters) => {
      const priceVariants = getters.prices?.[9460]; // G640 White
      return priceVariants?.[27182]; // Size M
    },

    assumptiveTotalsNoDiscount: (_, getters) =>
      (getters.assumptiveDefaultVariant?.[0] || 0) * getters.assumptiveQuantity,

    assumptiveGetBulkPrice: (_, getters) => variantPrices => {
      const currentThreshold = Object.keys(variantPrices).findLast(
        val => val <= getters.assumptiveTotalsNoDiscount
      );
      return Number(variantPrices[currentThreshold]);
    },

    assumptiveTotals: (_, getters) => {
      const pricePerPiece = getters.assumptiveGetBulkPrice(getters.assumptiveDefaultVariant);
      return pricePerPiece * getters.assumptiveQuantity;
    },

    assumptiveSavings: (_, getters) =>
      getters.assumptiveTotalsNoDiscount - getters.assumptiveTotals,

    assumptiveSavingsPercentage: (_, getters) =>
      Number((getters.assumptiveSavings / getters.assumptiveTotalsNoDiscount) * 100).toFixed(0),

    productsCompositionsCount: (_, getters) => id => {
      const { getProductById, compositionsQtys } = getters;
      const product = getProductById(id);
      if (!product) return 0;

      return product.compositions.reduce((acc, comp) => acc + (compositionsQtys[comp.id] || 0), 0);
    },

    variantPrice: (_, getters) => (compositionId, variantId) => {
      const variantPrices = getters.prices[compositionId]?.[variantId];
      return getters.assumptiveGetBulkPrice(variantPrices);
    },

    inventory: state => state.inventory,

    variantPriceForVariants: (_, getters) => (compositionId, variantIds) => {
      let price = 0;

      variantIds.some(variantId => {
        const variantPrices = getters.prices[compositionId]?.[variantId];
        if (!variantPrices) return false;
        price = getters.assumptiveGetBulkPrice(variantPrices);
        return price != null && price !== 0;
      });

      return price;
    },

    initialProduct: state => state.initialProduct,

    isSingleProductPage: (_, getters) => Boolean(getters.initialProduct),

    initialSelection: state => state.initialSelection
  },

  mutations: {
    setProductsAndDefaults(state, data) {
      const {
        fastestDeliverableTshirts,
        fastestSweatshirts,
        trending,
        prices,
        freeShippingThreshold,
        bulkDiscountThreshold
      } = data;
      const allProducts = [
        ...data.fastestDeliverableTshirts,
        ...data.fastestSweatshirts,
        ...data.trending
      ];
      const preselectedProductId = state.initialProduct?.id || state.initialSelection?.productId;
      const preselectedCompositioId =
        state.initialProduct?.compositionId || state.initialSelection?.compositionId;
      const defaultProduct =
        allProducts.find(product => product.id === preselectedProductId) ||
        fastestDeliverableTshirts[0];
      state.topItems = setDefaultFallback(fastestDeliverableTshirts);
      state.fastestItems = setDefaultFallback(fastestSweatshirts);
      state.trending = setDefaultFallback(trending);
      state.freeShippingThreshold = freeShippingThreshold;
      state.bulkDiscountThreshold = bulkDiscountThreshold;
      if (!state.selectedProduct) {
        state.selectedProduct = defaultProduct;
        state.selectedComposition =
          defaultProduct.compositions.find(comp => comp.id === preselectedCompositioId) ||
          defaultProduct.compositions.find(comp => comp.default);
      }
      state.prices = {
        ...state.prices,
        ...prices
      };
    },
    setProductDefaults(state, data) {
      state.selectedProduct = data;
      state.selectedComposition =
        data.compositions.find(comp => comp.id === state.initialProduct?.compositionId) ||
        data.compositions.find(comp => comp.default);
      state.prices = {
        ...state.prices,
        ...data.prices
      };
      state.freeShippingThreshold = data.freeShippingThreshold;
      state.bulkDiscountThreshold = data.bulkDiscountThreshold;
      state.topItems = [data];
    },
    setOrderData(state, data) {
      const { compositions } = data;
      if (!compositions) return;
      const currentCompositions = Object.keys(compositions);
      state.variantQuantities = currentCompositions.reduce(
        (res, compId) => ({
          ...res,
          [compId]: compositions[compId].variants
        }),
        {}
      );
    },
    setSelectedComposition(state, composition) {
      state.selectedComposition = composition;
      state.productsSelectedComposition = {
        ...state.productsSelectedComposition,
        [state.selectedProduct.id]: composition.id
      };
    },
    setSelectedProduct(state, product) {
      state.selectedProduct = product;
      if (!product) return;
      const { compositions } = product;
      const hasPreselectedComposition = state.productsSelectedComposition[product.id];
      state.selectedComposition = compositions.find(comp => {
        if (hasPreselectedComposition) {
          return comp.id === state.productsSelectedComposition[product.id];
        }
        return comp.default;
      });
    },
    updateBlankPrices(state, prices) {
      state.prices = {
        ...state.prices,
        ...prices
      };
    },
    updateVariantQuantity(state, { compositionId, id, quantity }) {
      state.variantQuantities = {
        ...state.variantQuantities,
        [compositionId]: {
          ...state.variantQuantities[compositionId],
          [id]: quantity
        }
      };
    },
    removeComposition(state, { compositionId }) {
      const nullifiedComposition = Object.keys(state.variantQuantities[compositionId]).reduce(
        (acct, key) => ({ ...acct, [key]: 0 }),
        {}
      );
      state.variantQuantities = {
        ...state.variantQuantities,
        [compositionId]: nullifiedComposition
      };
    },
    setAssumptiveQuantity(state, value) {
      state.assumptiveQuantity = value;
    },
    addToInventory(state, { product, inventoryQuantities }) {
      state.inventory = { ...state.inventory, [product.id]: inventoryQuantities };
    },
    setInitialProduct(state, initialProduct) {
      state.initialProduct = initialProduct;
    },
    setInitialSelection(state, initialSelection) {
      state.initialSelection = initialSelection;
    }
  },

  actions: {
    async fetchProduct({ commit }, initialProduct) {
      commit("setInitialProduct", initialProduct);
      const response = await api.printOnDemand.getProduct({
        catalog_number: initialProduct.catalogNumber
      });
      commit("setProductDefaults", objNamesToCamelCase(response));
    },
    async fetchProducts({ commit, dispatch }) {
      commit("loading/setFetchProducts", true, { root: true });
      const response = await api.printOnDemand.getProducts();
      commit("setProductsAndDefaults", objNamesToCamelCase(response));
      dispatch("checkInitialSelection");
      commit("loading/setFetchProducts", false, { root: true });
    },
    async fetchCompositionPrice({ commit }, id) {
      const { prices } = await api.printOnDemand.getCompositionPrices({ id });
      commit("updateBlankPrices", objNamesToCamelCase(prices));
    },
    async fetchAllCompositionsPrices({ commit, getters }) {
      const { compositionsWithQty } = getters;
      if (!compositionsWithQty.length) return;
      const { prices } = await api.printOnDemand.getCompositionPrices({
        id: compositionsWithQty.join(",")
      });
      commit("updateBlankPrices", objNamesToCamelCase(prices));
    },
    async handleCompositionSelect({ getters, commit, dispatch }, composition) {
      commit("setSelectedComposition", composition);
      if (!getters.prices[composition.id]) {
        await dispatch("fetchCompositionPrice", composition.id);
      }
    },
    async fetchInventory({ commit }, product) {
      const response = await api.jiffyshirts.product.inventory({ slug: product.id });
      commit("addToInventory", { product, inventoryQuantities: response });
    },
    handleProductSelect({ getters, commit, dispatch }, product) {
      commit("setSelectedProduct", product);
      dispatch("handleCompositionSelect", getters.selectedComposition);
      if (!getters.inventory[product.id]) {
        dispatch("fetchInventory", product);
      }
    },
    handleQtyUpdate({ commit, dispatch, getters }, data) {
      commit("updateVariantQuantity", {
        compositionId: getters.selectedCompositionId,
        ...data
      });
      dispatch("checkout/updateComposition", null, { root: true });
    },
    removeComposition({ commit, dispatch }, compositionId) {
      commit("removeComposition", { compositionId });
      dispatch("checkout/updateComposition", null, { root: true });
    },
    updateAssumptiveQuantity({ commit, getters }, value) {
      const oldValue = getters.assumptiveQuantity;
      commit("setAssumptiveQuantity", value);

      track(EVENTS.CHANGE.QUANTITY_SLIDER, {
        old_value: oldValue,
        new_value: value,
        saved_amount_value: getters.assumptiveSavings,
        saved_amount_percentage: getters.assumptiveSavingsPercentage
      });
    },
    checkInitialSelection({ commit, dispatch, getters }) {
      const { initialSelection, allProducts } = getters;

      if (initialSelection?.productId) {
        const selectedProduct = allProducts.find(
          product => product.id === initialSelection.productId
        );
        const selectedComposition =
          selectedProduct?.compositions?.find(
            composition => composition.id === initialSelection.compositionId
          ) || selectedProduct?.compositions?.find(composition => composition.default);

        if (selectedComposition) {
          commit("setSelectedComposition", selectedComposition);
        }

        if (selectedProduct) {
          dispatch("handleProductSelect", selectedProduct);
        }
      }

      commit("setInitialSelection", null);
    }
  }
};

export default productModule;
